<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <GymTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        :search-fn="globalSearch"
        @select-member="handleSelectMember"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="members-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('members.title') }}</h1>
        </div>
        <IntercomButton variant="primary" @click="$router.push('/members/new')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('members.newMember') }}</span>
        </IntercomButton>
      </div>

      <!-- Filters -->
      <div class="filters-bar">
        <div class="search-input">
          <IntercomInput
            v-model="searchQuery"
            :placeholder="$t('members.searchPlaceholder')"
            @input="debouncedSearch"
          />
        </div>

        <div class="status-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            class="status-tab"
            :class="{ active: statusFilter === tab.value }"
            @click="statusFilter = tab.value"
          >
            {{ $t(tab.label) }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <IntercomSpinner />
      </div>

      <!-- Empty State -->
      <div v-else-if="members.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </div>
        <h3>{{ $t('members.noMembers') }}</h3>
        <p>{{ $t('members.addFirstMember') }}</p>
        <IntercomButton variant="primary" @click="$router.push('/members/new')">
          {{ $t('members.newMember') }}
        </IntercomButton>
      </div>

      <!-- Members Table -->
      <IntercomCard v-else class="members-table-card">
        <table class="members-table">
          <thead>
            <tr>
              <th class="avatar-col"></th>
              <th>{{ $t('members.memberNumber') }}</th>
              <th>{{ $t('members.name') }}</th>
              <th>{{ $t('members.phone') }}</th>
              <th>{{ $t('members.plan') }}</th>
              <th>{{ $t('members.membershipEnd') }}</th>
              <th>{{ $t('common.status') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in members"
              :key="member.id"
              @click="$router.push(`/members/${member.id}`)"
            >
              <td class="avatar-col">
                <MemberAvatar
                  :photo-url="member.photo_url"
                  :name="member.name"
                  size="sm"
                />
              </td>
              <td class="member-number">{{ member.member_number }}</td>
              <td class="member-name">{{ member.name }}</td>
              <td>{{ member.phone }}</td>
              <td>{{ member.plan_name || '-' }}</td>
              <td>{{ formatDate(member.membership_end) }}</td>
              <td>
                <span class="status-badge" :class="getMemberStatus(member)">
                  {{ $t(`common.${getMemberStatus(member)}`) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <IntercomPagination
          :total="total"
          :limit="limit"
          :offset="offset"
          :item-label="$t('members.title').toLowerCase()"
          @update:offset="onPageChange"
        />
      </IntercomCard>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useGym } from '../composables/useGym.js'
import { useNavigation } from '../composables/useNavigation.js'
import { IntercomLayout, IntercomSidebar, IntercomCard, IntercomButton, IntercomInput, IntercomSpinner, IntercomPagination } from '@shared/components/intercom'
import GymTopbar from '../components/GymTopbar.vue'
import MemberAvatar from '../components/MemberAvatar.vue'
import dayjs from 'dayjs'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { user, globalUser, logout } = useAuth()
const { listMembers, globalSearch } = useGym()

const { navSections } = useNavigation()

// State
const searchQuery = ref('')
const statusFilter = ref(route.query.status || '')

// Paginated list
const { items: members, total, loading, offset, limit, fetch: fetchMembers, resetAndFetch } = usePaginatedList(
  (params) => listMembers(params),
  { pageSize: 25, immediate: false }
)

// Initial fetch with route query status
resetAndFetch({ status: statusFilter.value })

// Status tabs
const statusTabs = [
  { value: '', label: 'members.filterAll' },
  { value: 'active', label: 'members.filterActive' },
  { value: 'expiring', label: 'members.filterExpiring' },
  { value: 'expired', label: 'members.filterExpired' },
  { value: 'inactive', label: 'members.filterInactive' },
]

// Computed
const businessName = computed(() => user.value?.business?.name || '')

// Methods
const formatDate = (date) => {
  if (!date) return '-'
  return dayjs(date).format('DD MMM YYYY')
}

const getMemberStatus = (member) => {
  if (!member.is_active) return 'inactive'
  if (!member.membership_end) return 'inactive'
  const days = dayjs(member.membership_end).diff(dayjs(), 'day')
  if (days < 0) return 'expired'
  if (days <= 7) return 'expiring'
  return 'active'
}

let searchTimeout = null
const debouncedSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    resetAndFetch({ search: searchQuery.value, status: statusFilter.value })
  }, 300)
}

const onPageChange = (newOffset) => {
  offset.value = newOffset
  fetchMembers()
}

const handleSelectMember = (member) => {
  router.push(`/members/${member.id}`)
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

watch(statusFilter, () => {
  resetAndFetch({ search: searchQuery.value, status: statusFilter.value })
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.members-view {
  padding: var(--space-6);
  max-width: 1400px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.filters-bar {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  flex-wrap: wrap;
}

.search-input {
  width: 300px;
  max-width: 300px;
  flex-shrink: 0;
}

.search-input :deep(.intercom-input-wrapper) {
  width: 100%;
}

.status-tabs {
  display: flex;
  gap: var(--space-1);
  background: var(--color-gray-100);
  padding: var(--space-1);
  border-radius: var(--radius-md);
}

.status-tab {
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  cursor: pointer;
  transition: all 0.2s;
}

.status-tab:hover {
  color: var(--color-charcoal);
}

.status-tab.active {
  background: var(--color-white);
  color: var(--color-charcoal);
  box-shadow: var(--shadow-sm);
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-12);
}

.empty-state {
  text-align: center;
  padding: var(--space-12);
}

.empty-icon {
  color: var(--color-gray-300);
  margin-bottom: var(--space-4);
}

.empty-state h3 {
  font-size: var(--font-size-lg);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-2) 0;
}

.empty-state p {
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.members-table-card {
  overflow: hidden;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
}

.members-table th {
  text-align: left;
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  border-bottom: 1px solid var(--color-gray-200);
  background: var(--color-gray-50);
}

.avatar-col {
  width: 48px;
  padding: var(--space-2) var(--space-3) var(--space-2) var(--space-4);
}

.members-table td {
  padding: var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);
}

.members-table tbody tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.members-table tbody tr:hover {
  background: var(--color-gray-50);
}

.member-number {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.member-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.status-badge {
  display: inline-block;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.status-badge.active {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.status-badge.expiring {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

.status-badge.expired {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.status-badge.inactive {
  background: var(--color-gray-100);
  color: var(--color-gray-500);
}

@media (max-width: 768px) {
  .members-view {
    padding: var(--space-4);
  }

  .filters-bar {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .status-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
